import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,

  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,

  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import LoadingSpinner from '../../components/LoadingSpinner';
import DataQualityIndicator from '../../components/DataQualityIndicator';
import TradeChartModal from '../../components/TradeChartModal';
import KpiStrip from './results/KpiStrip';
import EquityDrawdownChart from './results/EquityDrawdownChart';
import TradesPanel from './results/TradesPanel';
import MonthlyHeatmap from './results/MonthlyHeatmap';
import { headlineFrom } from './results/headline';
import { withDisplayEquityCurve } from './utils/performanceCalculations';
import {
  fetchBacktestResults,
  clearCurrentBacktest
} from './backtestsSlice';
import DWLFLoader from '../../components/DWLFLoader';
import { isAbsent, formatCurrency, formatPercentage, formatNumber, absentColor } from '../../utils/metricFormat';

const BacktestResultsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requestId } = useParams();
  
  const {
    currentBacktest,
    currentResults,
    resultsLoading,
    resultsError
  } = useSelector((state) => state.backtests);

  // Local state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('all');
  
  // Trade chart modal state
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);

  // Normalize results to always be an array of symbol results
  const allResults = useMemo(() => {
    if (!currentResults) return [];
    return Array.isArray(currentResults) ? currentResults : [currentResults];
  }, [currentResults]);

  // List of available symbols from results (deduplicated)
  const availableResultSymbols = useMemo(() => {
    const symbols = allResults.map(r => r.symbol).filter(Boolean);
    return Array.from(new Set(symbols)); // Remove duplicates
  }, [allResults]);
  const resultsWithSymbols = useMemo(() => {
    return allResults.filter(r => r.symbol);
  }, [allResults]);

  // Returns NULL for an absent percentage, never 0. Returning 0 here is what
  // made a missing max-drawdown render as "0.00%" in GREEN — the helper silently
  // manufactured a plausible risk figure at the one point every caller passes
  // through. Callers must handle null; the formatters render it as "—".
  const normalizePercentToDecimal = (value) =>
    // isAbsent, not the loose global isNaN — isNaN coerces, so isNaN('') is
    // false and an empty string would have sailed through as 0.
    (isAbsent(value) ? null : value / 100);

  // Compute resultsData based on selectedSymbol
  const resultsData = useMemo(() => {
    if (allResults.length === 0) return null;

    if (selectedSymbol !== 'all') {
      // Single symbol view — find matching result. Only fall back to the
      // portfolio curve for a genuinely single-symbol run (no symbol selector):
      // for a symbol drilled into within a MULTI-symbol portfolio run, the
      // shared-capital portfolio curve is not this symbol's curve, and showing
      // it against this symbol's trades would be misleading.
      const match = allResults.find(r => r.symbol === selectedSymbol) || allResults[0];
      return allResults.length === 1 ? withDisplayEquityCurve(match) : match;
    }

    // "All" view — merge all symbol results. A single-symbol run made in portfolio mode still takes
    // the portfolio path below, so its headline numbers, curve and drawdown share one basis
    // (portfolioPerformance); taking the per-symbol row here mixed the two (DWLF-311).
    const single = allResults.length === 1 ? allResults[0] : null;
    if (single && !(single.portfolioPerformance && single.portfolioEquityCurve?.length)) return withDisplayEquityCurve(single);

    // Portfolio-mode runs (dwlf-scheduled-jobs#205 + serverless-portfolio-
    // tracker#435/#436) produce a real shared-capital equity curve and a
    // dedicated portfolioPerformance summary. When present, prefer those
    // over the synthetic equal-weight-summed view below, because:
    //   - The summed view's equity curve starts at N × initialCapital and
    //     adds the dollar P&L of N independent accounts (~$110k for an
    //     11-symbol run). That's not what a single account would actually
    //     experience.
    //   - aggregatePerformance.averageReturn is the mean of per-symbol
    //     returnPercent (so it answers "what's the average per-symbol %"
    //     not "what % did this $10k make").
    //   - portfolioPerformance.maxDrawdownPercent reflects correlated
    //     cluster drawdowns; aggregatePerformance only shows the worst
    //     single-symbol DD, which can be materially lower.
    const portfolioPerformance = allResults[0]?.portfolioPerformance;
    const portfolioEquityCurve = allResults[0]?.portfolioEquityCurve;
    const usePortfolioView = Boolean(
      portfolioPerformance && portfolioEquityCurve && portfolioEquityCurve.length > 0
    );

    // Merge trades from all symbols, ensuring each trade carries its source symbol
    const mergedTrades = usePortfolioView
      ? (allResults[0]?.portfolioTrades || []).map(t => ({ ...t, symbol: t.symbol }))
      : allResults.flatMap(r =>
        (r.trades || []).map(t => ({ ...t, symbol: t.symbol || r.symbol }))
      );

    // Use aggregate performance from the first result (backend attaches it to each row)
    const aggregate = allResults[0]?.aggregatePerformance || {};

    // Equity curve: prefer the single shared-capital curve when portfolio
    // mode was on. Fall back to summing per-symbol curves for backward
    // compatibility with runs that didn't request portfolio mode.
    let mergedEquityCurve;
    if (usePortfolioView) {
      mergedEquityCurve = portfolioEquityCurve.map(point => ({
        date: point.date,
        equity: point.equity,
        cash: point.cash,
        openPositions: point.openPositions,
        deployedCapital: point.deployedCapital,
        symbol: 'Portfolio',
      }));
    } else {
      const equityCurveMap = new Map();
      allResults.forEach(r => {
        if (r.equityCurve && r.equityCurve.length > 0) {
          r.equityCurve.forEach(point => {
            const date = point.date;
            if (!equityCurveMap.has(date)) {
              equityCurveMap.set(date, { date, equity: 0, symbol: 'All' });
            }
            equityCurveMap.get(date).equity += point.equity || 0;
          });
        }
      });
      mergedEquityCurve = Array.from(equityCurveMap.values())
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    // Merge monthly returns (aggregate by year-month, averaging returns)
    const monthlyReturnsMap = new Map();
    allResults.forEach(r => {
      (r.monthlyReturns || []).forEach(monthly => {
        const key = monthly.yearMonth || `${monthly.year}-${String(monthly.month).padStart(2, '0')}`;
        if (monthlyReturnsMap.has(key)) {
          const existing = monthlyReturnsMap.get(key);
          monthlyReturnsMap.set(key, {
            ...existing,
            returnPercent: existing.returnPercent + (monthly.returnPercent || 0),
            symbolCount: existing.symbolCount + 1
          });
        } else {
          monthlyReturnsMap.set(key, { 
            ...monthly, 
            returnPercent: monthly.returnPercent || 0,
            symbolCount: 1
          });
        }
      });
    });
    // Average the returns by symbol count and remove the symbolCount field
    const mergedMonthlyReturns = Array.from(monthlyReturnsMap.values()).map(entry => ({
      ...entry,
      returnPercent: entry.returnPercent / entry.symbolCount
    })).map(({ symbolCount, ...rest }) => rest);

    // Headline figures are the API's: portfolioPerformance, else aggregatePerformance (the equal-weight
    // per-symbol average). No trade-weighted fallback is computed here; absent stays null (invariant 8).
    // Performance summary: portfolioPerformance wins when present (real
    // shared-capital totals from one $10k account), falling back to
    // aggregatePerformance (equal-weight per-symbol average) for legacy
    // runs without portfolio mode.
    // ⚠️ No `?? 0` here. This object BECOMES resultsData.performance, i.e. the
    // `backendMetrics` read further down — so a zero fabricated at this level
    // makes every downstream absence check dead code. The percentage path was
    // already absence-preserving via normalizePercentToDecimal; the ABSOLUTE
    // values (totalReturn, sharpeRatio, maxDrawdown) were not, which is a
    // parallel path that fixing only the choke point missed.
    const performance = usePortfolioView ? {
      totalReturn: portfolioPerformance.totalReturn ?? null,
      totalReturnPercent: normalizePercentToDecimal(portfolioPerformance.totalReturnPercent),
      winRate: normalizePercentToDecimal(portfolioPerformance.winRate),
      profitFactor: portfolioPerformance.profitFactor ?? null,
      sharpeRatio: portfolioPerformance.sharpeRatio ?? null,
      maxDrawdown: portfolioPerformance.maxDrawdown ?? null,
      maxDrawdownPercent: normalizePercentToDecimal(portfolioPerformance.maxDrawdownPercent),
    } : {
      totalReturn: aggregate.totalReturn ?? null,
      totalReturnPercent: normalizePercentToDecimal(aggregate.averageReturn ?? aggregate.totalReturnPercent),
      winRate: normalizePercentToDecimal(aggregate.averageWinRate ?? aggregate.winRate),
      profitFactor: aggregate.profitFactor ?? null,
      sharpeRatio: aggregate.sharpeRatio ?? null,
      maxDrawdown: aggregate.maxDrawdown ?? null,
      maxDrawdownPercent: normalizePercentToDecimal(aggregate.maxDrawdownPercent),
    };

    return {
      symbol: 'ALL',
      totalTrades: usePortfolioView ? (portfolioPerformance.totalTrades ?? null) : (aggregate.totalTrades ?? null),
      trades: mergedTrades,
      equityCurve: mergedEquityCurve,
      monthlyReturns: mergedMonthlyReturns,
      winRate: usePortfolioView ? portfolioPerformance.winRate : (aggregate.averageWinRate ?? aggregate.winRate ?? null),
      totalReturnPercent: usePortfolioView ? portfolioPerformance.totalReturnPercent : (aggregate.averageReturn ?? aggregate.totalReturnPercent ?? null),
      performance,
      aggregatePerformance: aggregate,
      // Keep the portfolio block on the resulting object so other parts of
      // the page (rejected-signal counts, capital utilization etc.) can read
      // it directly without having to re-derive from allResults[0].
      portfolioPerformance: usePortfolioView ? portfolioPerformance : null,
      portfolioRejectedSignals: usePortfolioView ? (allResults[0]?.portfolioRejectedSignals || []) : null,
      // Mode marker so downstream components can show "Portfolio mode"
      // labels / caveats without re-checking field presence.
      mode: usePortfolioView ? 'portfolio' : 'aggregate',
    };
  }, [allResults, selectedSymbol]);

  // Fetch backtest results on component mount
  useEffect(() => {
    if (requestId) {
      dispatch(fetchBacktestResults(requestId));
    }
    
    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentBacktest());
    };
  }, [dispatch, requestId]);
  
  useEffect(() => {
    setSelectedSymbol('all');
  }, [requestId]);

  useEffect(() => {
    if (selectedSymbol !== 'all' && !availableResultSymbols.includes(selectedSymbol)) {
      setSelectedSymbol('all');
    }
  }, [availableResultSymbols, selectedSymbol]);

  // Auto-refresh for processing backtests
  useEffect(() => {
    if (currentBacktest?.status === 'processing' || currentBacktest?.status === 'pending') {
      const interval = setInterval(() => {
        dispatch(fetchBacktestResults(requestId));
      }, 5000); // Poll every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [dispatch, requestId, currentBacktest?.status]);

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status color and icon
  const getStatusInfo = (status) => {
    switch (status) {
    case 'completed':
      return { color: 'success', icon: <CheckCircleIcon />, text: 'Completed' };
    case 'processing':
      return { color: 'info', icon: <InfoIcon />, text: 'Processing' };
    case 'pending':
      return { color: 'warning', icon: <WarningIcon />, text: 'Pending' };
    case 'failed':
      return { color: 'error', icon: <ErrorIcon />, text: 'Failed' };
    default:
      return { color: 'default', icon: <InfoIcon />, text: status };
    }
  };


  // The API's net figures in the fields TradeChartModal and the CSV read (pnl, returnPercent as a
  // fraction, duration). Absent stays absent: the modal is told not to derive its own (derivePnl).
  const normalizeTrade = (t) => ({
    ...t,
    pnl: t.netPnL ?? t.grossPnL ?? t.pnl ?? null,
    returnPercent: (t.netPnLPercent ?? t.returnPercent) == null ? null : (t.netPnLPercent ?? t.returnPercent) / 100,
    duration: t.holdingPeriod ?? t.duration ?? null,
  });
  const normalizedTrades = resultsData?.trades?.map(normalizeTrade) || [];

  // CSV export order: newest first, as the trades panel shows them.
  normalizedTrades.sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate));

  // Handle trade row click
  const handleTradeClick = (trade) => {
    setSelectedTrade(normalizeTrade(trade));
    setTradeModalOpen(true);
  };

  // Handle trade modal close
  const handleTradeModalClose = () => {
    setTradeModalOpen(false);
    setSelectedTrade(null);
  };

  // Handle export functionality
  const handleExportCSV = () => {
    if (!resultsData?.trades) {
      setSnackbarMessage('No trade data available for export');
      setSnackbarOpen(true);
      return;
    }

    const headers = ['Symbol', 'Type', 'Entry Date', 'Exit Date', 'Entry Price', 'Exit Price', 'Quantity', 'P&L', 'Return %', 'Duration'];
    const csvContent = [
      headers.join(','),
      ...normalizedTrades.map(trade => [
        trade.symbol,
        trade.direction ?? trade.type,
        trade.entryDate,
        trade.exitDate,
        trade.entryPrice,
        trade.exitPrice,
        trade.quantity,
        trade.pnl == null ? '' : trade.pnl.toFixed(2), // absent is an empty cell, not 0
        trade.returnPercent == null ? '' : formatPercentage(trade.returnPercent),
        trade.duration ?? ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backtest-trades-${requestId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    setSnackbarMessage('Trade data exported successfully');
    setSnackbarOpen(true);
  };

  // Loading state
  if (resultsLoading && !resultsData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <IconButton
            onClick={() => navigate('/backtests')}
            sx={{ 
              mr: 2, 
              borderRadius: 0,
              border: '1px solid var(--dwlf-ink-10)',
              '&:hover': {
                borderColor: 'var(--dwlf-accent)',
                backgroundColor: 'var(--dwlf-accent-05)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Backtest Results
          </Typography>
        </Box>
        <LoadingSpinner message="Loading backtest results..." />
      </Container>
    );
  }

  // Error state
  if (resultsError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <IconButton
            onClick={() => navigate('/backtests')}
            sx={{ 
              mr: 2, 
              borderRadius: 0,
              border: '1px solid var(--dwlf-ink-10)',
              '&:hover': {
                borderColor: 'var(--dwlf-accent)',
                backgroundColor: 'var(--dwlf-accent-05)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Backtest Results
          </Typography>
        </Box>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }}>
          <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 500 }}>Failed to load backtest results</Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>{resultsError}</Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => dispatch(fetchBacktestResults(requestId))}
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 0,
            backgroundColor: 'var(--dwlf-accent)',
            color: 'var(--dwlf-bg)',
            '&:hover': { backgroundColor: 'var(--dwlf-text)' },
          }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  // No data state
  if (!currentBacktest || !resultsData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <IconButton
            onClick={() => navigate('/backtests')}
            sx={{ 
              mr: 2, 
              borderRadius: 0,
              border: '1px solid var(--dwlf-ink-10)',
              '&:hover': {
                borderColor: 'var(--dwlf-accent)',
                backgroundColor: 'var(--dwlf-accent-05)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Backtest Results
          </Typography>
        </Box>
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          No backtest data found. Please check the backtest ID and try again.
        </Alert>
      </Container>
    );
  }

  const statusInfo = getStatusInfo(currentBacktest.status);
  // Drilled into one symbol of a multi-symbol run: that symbol's own figures, never the portfolio's.
  const headline = headlineFrom(resultsData, { perSymbol: selectedSymbol !== 'all' && allResults.length > 1 });

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header — strategy, run parameters and data quality in one place (DWLF-306) */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/backtests')} aria-label="Back to backtests" sx={{ borderRadius: 0, border: '1px solid var(--dwlf-ink-12)' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--dwlf-accent)' }}>
              Backtest · {statusInfo.text}{currentBacktest.updatedAt ? ` ${formatDate(currentBacktest.updatedAt)}` : ''}
            </Typography>
            <Typography component="h1" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600, fontSize: { xs: '1.5rem', md: '1.875rem' }, letterSpacing: '-0.02em', color: 'var(--dwlf-text)', m: 0 }}>
              {currentBacktest.strategyName}
            </Typography>
            <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8125rem', color: 'var(--dwlf-ink-60)', mt: 0.5 }}>
              {(currentBacktest.symbols || []).join(' · ')}
              {currentBacktest.timeframe ? ` · ${currentBacktest.timeframe}` : ''}
              {' · '}{formatDate(currentBacktest.startDate)} → {formatDate(currentBacktest.endDate)}
              {' · '}{formatCurrency(currentBacktest.initialCapital)}
              {' · risk '}{formatPercentage(currentBacktest.riskPerTrade)}/trade
              {currentBacktest.maxDrawdown ? ` · DD limit ${formatPercentage(currentBacktest.maxDrawdown)}` : ''}
            </Typography>
            {resultsData?.equityCurve && (
              <Box sx={{ mt: 1 }}><DataQualityIndicator equityCurve={resultsData.equityCurve} /></Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {currentBacktest.strategyId && (
              <Button variant="outlined" onClick={() => navigate(`/strategies/${currentBacktest.strategyId}/view`)} sx={{ borderRadius: 0 }}>Open strategy</Button>
            )}
            <Tooltip title="Export trades (CSV)">
              <span>
                <IconButton onClick={handleExportCSV} disabled={!resultsData?.trades} aria-label="Export trades as CSV" sx={{ borderRadius: 0, border: '1px solid var(--dwlf-ink-12)' }}>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reload results">
              <IconButton onClick={() => dispatch(fetchBacktestResults(requestId))} aria-label="Reload results" sx={{ borderRadius: 0, border: '1px solid var(--dwlf-ink-12)' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>


        {/* Processing indicator */}
        {(currentBacktest?.status === 'processing' || currentBacktest?.status === 'pending') && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 0 }}>
            <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.85rem' }}>
              Backtest is currently processing. Results will update automatically...
            </Typography>
            <DWLFLoader size="small" showText={false} />
          </Alert>
        )}

        {/* Symbol Selector — shown when multiple symbols */}
        {availableResultSymbols.length > 1 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              View Results By Symbol
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip
                label="All Symbols"
                color={selectedSymbol === 'all' ? 'primary' : 'default'}
                variant={selectedSymbol === 'all' ? 'filled' : 'outlined'}
                onClick={() => { setSelectedSymbol('all'); }}
              />
              {availableResultSymbols.map((sym) => (
                <Chip
                  key={sym}
                  label={sym}
                  color={selectedSymbol === sym ? 'primary' : 'default'}
                  variant={selectedSymbol === sym ? 'filled' : 'outlined'}
                  onClick={() => { setSelectedSymbol(sym); }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Symbol Performance Comparison — shown when multiple symbols */}
        {availableResultSymbols.length > 1 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Symbol Performance Comparison
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Total Trades</TableCell>
                    <TableCell align="right">Win Rate</TableCell>
                    <TableCell align="right">Return %</TableCell>
                    <TableCell align="right">Profit Factor</TableCell>
                    <TableCell align="right">Max Drawdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultsWithSymbols.map((r, index) => {
                    const winRate = r.performance?.winRate ?? normalizePercentToDecimal(r.winRate);
                    const totalReturnPercent =
                      r.performance?.totalReturnPercent ?? normalizePercentToDecimal(r.totalReturnPercent);
                    const maxDrawdownPercent =
                      r.performance?.maxDrawdownPercent ?? normalizePercentToDecimal(r.maxDrawdownPercent);

                    return (
                      <TableRow
                        key={`${r.symbol}-${index}`}
                        hover
                        onClick={() => { setSelectedSymbol(r.symbol); }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Chip label={r.symbol} size="small" />
                        </TableCell>
                        <TableCell align="right">{r.totalTrades || 0}</TableCell>
                        <TableCell align="right">
                          {formatPercentage(winRate, 1)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={isAbsent(totalReturnPercent) ? absentColor : (totalReturnPercent >= 0 ? 'success.main' : 'error.main')}
                            variant="body2"
                            fontWeight="medium"
                          >
                            {formatPercentage(totalReturnPercent)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(r.performance?.profitFactor ?? r.profitFactor)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercentage(maxDrawdownPercent)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Headline numbers — one basis, named (DWLF-306/311) */}
        {resultsData && (
          <Box sx={{ mb: 3 }}>
            <KpiStrip headline={headline} />
          </Box>
        )}

        {/* Equity & drawdown on one time axis, beside the trades */}
        {resultsData && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 400px' }, gap: 3, mb: 3, alignItems: 'start' }}>
            <EquityDrawdownChart equityCurve={resultsData.equityCurve} trades={resultsData.trades} maxDrawdownPct={headline?.maxDrawdownPct} />
            <TradesPanel trades={resultsData.trades} onTradeClick={handleTradeClick} onExport={resultsData.trades?.length ? handleExportCSV : null} />
          </Box>
        )}

        {/* Monthly returns */}
        {resultsData && (
          <Box sx={{ mb: 3 }}>
            <MonthlyHeatmap monthlyReturns={resultsData.monthlyReturns} />
          </Box>
        )}


        {/* No trades message */}
        {(!resultsData.trades || resultsData.trades.length === 0) && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Trade History
            </Typography>
            <Alert severity="info">
              No trades were executed during this backtest period.
            </Alert>
          </Paper>
        )}
      </motion.div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Trade Chart Modal */}
      <TradeChartModal
        open={tradeModalOpen}
        onClose={handleTradeModalClose}
        derivePnl={false}
        trade={selectedTrade}
      />
    </Container>
  );
};

export default BacktestResultsPage; 
